import React from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  Grid,
  TextField,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { GiftProduct, GiftProductType, IntegrationType } from '../../../../models';
import { CurrencyInput } from '../../../../components/mui/inputs';

interface PlaceConfig {
  placeId: string;
  placeName: string;
  integration?: string | null;
  partnerValue: number | null;
  hasProductPlace: boolean;
  rtiCode?: string | null;
  productRtiCode?: string | null;
  isEvoActive?: boolean;
  trevoServiceId?: number | null;
  discountPercentage?: number | null;
}

interface ProductPlaceItemProps {
  placeConfig: PlaceConfig;
  product: GiftProduct;
  onDelete: () => void;
  onUpdatePartnerValue: (value: number) => void;
  onUpdateRtiCode: (value: string) => void;
  onUpdateEvoActive: (value: boolean) => void;
  onUpdateTrevoServiceId: (value: number | null) => void;
  onUpdateDiscountPercentage?: (value: number) => void;
  fuels?: any[];
}

const ProductPlaceItem: React.FC<ProductPlaceItemProps> = ({
  placeConfig,
  product,
  onDelete,
  onUpdatePartnerValue,
  onUpdateRtiCode,
  onUpdateEvoActive,
  onUpdateTrevoServiceId,
  onUpdateDiscountPercentage,
  fuels = [],
}) => {
  // Para FuelDiscount, não mostramos chips de Automático/Manual nem campo de valor do parceiro
  // pois o conceito de "valor do parceiro" não se aplica a descontos no combustível
  const isFuelDiscount = product.type === GiftProductType.FuelDiscount;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: !isFuelDiscount && placeConfig.hasProductPlace ? 'success.main' : 'divider',
        bgcolor: !isFuelDiscount && placeConfig.hasProductPlace ? 'success.lighter' : 'background.paper',
        mb: 1.5,
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={isFuelDiscount ? 0 : 2}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Typography variant="body1" fontWeight={600}>
            {placeConfig.placeName}
          </Typography>
          {/* Chips de Automático/Manual só aparecem para produtos que não são FuelDiscount */}
          {!isFuelDiscount && placeConfig.hasProductPlace && (
            <Chip
              icon={<CheckCircleIcon />}
              label="Automático"
              size="small"
              color="success"
              sx={{ fontWeight: 600 }}
            />
          )}
          {!isFuelDiscount && !placeConfig.hasProductPlace && (
            <Chip
              icon={<WarningIcon />}
              label="Manual"
              size="small"
              color="warning"
              sx={{ fontWeight: 600 }}
            />
          )}
        </Box>
        <IconButton
          size="small"
          onClick={onDelete}
          sx={{
            '&:hover': {
              bgcolor: 'error.lighter',
              color: 'error.main',
            },
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Grid de configurações só aparece se não for FuelDiscount (que não tem valor do parceiro) */}
      {!isFuelDiscount && (
      <Grid container spacing={2}>
        <Grid size={12}>
          <CurrencyInput
            fullWidth
            label="Valor do parceiro"
            value={placeConfig.partnerValue ?? 0}
            onChange={onUpdatePartnerValue}
            disabled={placeConfig.hasProductPlace}
            helperText={
              placeConfig.hasProductPlace
                ? 'Valor automático do catálogo de produtos'
                : 'O valor que o parceiro receberá por este produto'
            }
            sx={{
              '& .MuiInputBase-root': {
                bgcolor: placeConfig.hasProductPlace ? 'success.lighter' : 'background.paper',
              },
            }}
          />
        </Grid>

        {product.type === GiftProductType.Virtual && onUpdateDiscountPercentage && (
          <Grid size={12}>
            <TextField
              fullWidth
              label="Percentual de Desconto (%)"
              type="number"
              value={placeConfig.discountPercentage ?? ''}
              onChange={(e) => {
                const val = e.target.value;
                onUpdateDiscountPercentage(val === '' ? 0 : parseFloat(val));
              }}
              error={placeConfig.discountPercentage !== undefined && (placeConfig.discountPercentage < 0 || placeConfig.discountPercentage > 100)}
              inputProps={{
                step: 0.01,
                min: 0,
                max: 100,
              }}
              helperText={
                placeConfig.discountPercentage !== undefined && (placeConfig.discountPercentage < 0 || placeConfig.discountPercentage > 100)
                  ? "O desconto deve estar entre 0 e 100"
                  : "Desconto aplicado sobre o valor do produto neste local"
              }
            />
          </Grid>
        )}

        {product.integrationType && (
          <Grid size={12}>
            <Box
              sx={{
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="caption" fontWeight={600} display="block" mb={1.5} color="text.secondary">
                Configuração de Integração
              </Typography>

              {product.integrationType === IntegrationType.rti && (
                placeConfig.productRtiCode ? (
                  <TextField
                    fullWidth
                    size="small"
                    label="Código RTI"
                    value={placeConfig.productRtiCode}
                    disabled
                    helperText="Código automático do catálogo de produtos"
                    sx={{
                      '& .MuiInputBase-root': {
                        bgcolor: 'success.lighter',
                      },
                    }}
                  />
                ) : (
                  <TextField
                    fullWidth
                    size="small"
                    label="Código RTI"
                    value={placeConfig.rtiCode || ''}
                    onChange={(e) => onUpdateRtiCode(e.target.value)}
                    helperText="Código de integração com sistema RTI"
                  />
                )
              )}

              {product.integrationType === IntegrationType.evo && (
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip
                    label="EVO"
                    size="small"
                    color="success"
                    sx={{ fontWeight: 600 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Integração EVO ativa para este produto
                  </Typography>
                </Box>
              )}

              {product.integrationType === IntegrationType.trevo && (
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="ID do Serviço Trevo"
                  value={placeConfig.trevoServiceId ?? ''}
                  onChange={(e) => onUpdateTrevoServiceId(e.target.value ? Number(e.target.value) : null)}
                  helperText="ID do serviço no Top Banho (Rede Trevo)"
                />
              )}
            </Box>
          </Grid>
        )}
      </Grid>
      )}
    </Paper>
  );
};

export default ProductPlaceItem;
